import { Injectable } from "@angular/core";
import { Observable, ReplaySubject } from "rxjs";
import { mergeMap } from "rxjs/operators";
import { Profiles } from "../../modules/administration/models/profiles";
import { LogService } from "../../modules/shared/services/log.service";
import { AuthSessionService } from "./auth-session.service";

@Injectable({
  providedIn: 'root'
})
export class AuthSessionServiceDelegate implements AuthSessionService {
  private delegate: AuthSessionService = null;

  private LOG: LogService = new LogService(AuthSessionServiceDelegate);

  private _delegateSubject: ReplaySubject<AuthSessionService> = new ReplaySubject(1);

  constructor(
  ) { }

  /**
   * Returns true if and only if properly configured.
   */
  public isConfigured(): boolean {
    return this.delegate !== null;
  }

  /**
   * Configure for a specific delegate and auth method.
   */
  public configureWithDelegate(delegate: AuthSessionService) {
    if (!this.isConfigured()) { // Don't overwrite if already configured
      this.delegate = delegate;
    } else {
      this.LOG.warn("Attempting to reconfigure an already configured AuthSessionServiceDelegate. New configuration was ignored.");
    }

    this._delegateSubject.next(delegate);
  }

  /**
   * Reset the service. The service will need to be configured again before
   * it can be used.
   */
  private reset() {
    this.delegate = null
  }

  public setAccessTokenObject(accessToken: any): void {
    return this.delegate.setAccessTokenObject(accessToken);
  }

  public isReady(): Observable<boolean> {
    return this._delegateSubject.pipe(mergeMap(delegate => delegate.isReady()));
  }

  public configure(): Observable<void> {
    return this._delegateSubject.pipe(mergeMap(delegate => delegate.configure()));
  }

  public logOut() {
    // Ward against parallel logout calls,
    // for example, multiple API calls that end in 401
    if (this.isConfigured()) {
      this.delegate.logOut();
      this.reset();
    }
  }

  public logInfo() {
    return this.delegate.logInfo();
  }

  public getProfiles(): Observable<Profiles> {
    return this._delegateSubject.pipe(mergeMap(delegate => delegate.getProfiles()))
  }

  /**
   * Génère la liste des headers avec les données en session en fonction du type de connexion
   * @returns un JSON avec les headers pré-remplis
   */
  public getHeaders(): { [name: string]: string } {
    return this.delegate.getHeaders();
  }

  public isSwitchable(): Observable<boolean> {
    return this._delegateSubject.pipe(mergeMap(delegate => delegate.isSwitchable()));
  }
}
